import { inject, Injectable } from '@angular/core';
import { SqliteService } from '@app/services';
import { CheckInModel, RouteModel, RouteStoreModel, StoreModel } from '@app/models';
import { UserRepository } from './user.repository';
import { CheckInRepository } from './check-in.repository';
import { PaymentRepository } from './payment.repository';
import { differenceInSeconds } from 'date-fns';
import { PreorderedProductRepository } from './preordered-product.repository';
import { CartRepository } from './cart.repository';
import { ReturnRepository } from './return.repository';
import { PowerOfOneRepository } from '@app/repositories/power-of-one.repository';

@Injectable({
    providedIn: 'root',
})
export class StoreRepository
{
    private readonly keys = {
        tableName: 'stores',
        linkTableName: 'route_store_user',
    };
    private sqliteService = inject(SqliteService);
    private userRepository = inject(UserRepository);
    private checkInRepository = inject(CheckInRepository);
    private cartRepository = inject(CartRepository);
    private returnRepository = inject(ReturnRepository);
    private paymentRepository = inject(PaymentRepository);
    private preorderedProductRepository = inject(PreorderedProductRepository);
    private powerOfOneRepository = inject(PowerOfOneRepository);

    async getRouteStores(route: RouteModel): Promise<RouteStoreModel[]>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return [];
        }

        const sql = `
            SELECT s.*, rsu.sort_order
            FROM ${this.keys.tableName} s
                     JOIN ${this.keys.linkTableName} rsu ON s.external_id = rsu.store_external_id
            WHERE rsu.route_external_id = ?
              AND rsu.user_external_id = ?
            -- AND DATE (rsu.created_at) = DATE ('now') -- carefull with this, with new flow it might not be needed, it shows empty route for overdue routes otherwise
            ORDER BY rsu.sort_order;
        `;
        const data = await this.sqliteService.get(sql, [route.external_id, user.external_id]) ?? [];
        const stores = data as RouteStoreModel[];

        await Promise.all(stores.map(async (store) =>
        {
            store.has_preorder = false;
            store.payment = null;
            store.time_spent = '00:00:00';
            store.over_payment_amount = null;
            store.short_payment_amount = null;
            store.card_color = 'light';
            store.detail_icon_color = 'primary';
            store.today_check_in = null;
            store.detail_icon = 'chevron-forward-outline';
            store.sales_volume = 0;
            store.returns_volume = 0;

            const todayCheckIn = await this.checkInRepository.getTodayStoreCheckIn(store, route);

            if (todayCheckIn)
            {
                const overPaymentAmount = await this.paymentRepository.getOverPaidAmount(todayCheckIn.id, store, route);
                store.over_payment_amount = await this.paymentRepository.getOverPaidAmount(todayCheckIn.id, store, route);

                const shortPaymentAmount = await this.paymentRepository.getShortPaidAmount(todayCheckIn.id, store, route);
                store.short_payment_amount = await this.paymentRepository.getShortPaidAmount(todayCheckIn.id, store, route);

                if (todayCheckIn.check_out === null)
                {
                    store.detail_icon = 'timer-outline';
                    store.card_color = 'dark';
                    store.detail_icon_color = 'success';
                }
                else
                {
                    store.detail_icon = 'checkmark-done-outline';
                    store.card_color = 'success';
                    store.detail_icon_color = 'dark';
                }

                if (shortPaymentAmount !== null || overPaymentAmount !== null)
                {
                    store.card_color = 'warning';
                    store.detail_icon_color = 'dark';
                    store.detail_icon = 'cash-outline';
                }

                store.has_preorder = await this.preorderedProductRepository.isTherePreorderedProductsForTheStoreRoute(store, route);
                store.time_spent = this.getTimeSpent(todayCheckIn);
                store.payment = await this.paymentRepository.getPaymentByCheckIn(todayCheckIn.id, store.id, route.id);

                const cartItems = await this.cartRepository.loadCartItems(todayCheckIn.id, store, route);
                store.sales_volume = cartItems.reduce((acc, item) => acc + item.quantity, 0);

                const returnItems = await this.returnRepository.loadReturnItems(todayCheckIn.id, store, route);
                store.returns_volume = returnItems.reduce((acc, item) => acc + item.quantity, 0);
            }

            store.today_check_in = todayCheckIn;

            store.is_power_of_one = await this.powerOfOneRepository.isPowerOfOne(store);
        }));

        // for (const store of stores)
        // {
        //     store.has_preorder = false;
        //     store.payment = null;
        //     store.time_spent = '00:00:00';
        //     store.over_payment_amount = null;
        //     store.short_payment_amount = null;
        //     store.card_color = 'light';
        //     store.detail_icon_color = 'primary';
        //     store.today_check_in = null;
        //     store.detail_icon = 'chevron-forward-outline';
        //     store.sales_volume = 0;
        //     store.returns_volume = 0;
        //
        //     const todayCheckIn = await this.checkInRepository.getTodayStoreCheckIn(store, route);
        //
        //     if (todayCheckIn)
        //     {
        //         const overPaymentAmount = await this.paymentRepository.getOverPaidAmount(todayCheckIn.id, store, route);
        //         store.over_payment_amount = await this.paymentRepository.getOverPaidAmount(todayCheckIn.id, store, route);
        //
        //         const shortPaymentAmount = await this.paymentRepository.getShortPaidAmount(todayCheckIn.id, store, route);
        //         store.short_payment_amount = await this.paymentRepository.getShortPaidAmount(todayCheckIn.id, store, route);
        //
        //         if (todayCheckIn.check_out === null)
        //         {
        //             store.detail_icon = 'timer-outline';
        //             store.card_color = 'dark';
        //             store.detail_icon_color = 'success';
        //         }
        //         else
        //         {
        //             store.detail_icon = 'checkmark-done-outline';
        //             store.card_color = 'success';
        //             store.detail_icon_color = 'dark';
        //         }
        //
        //         if (shortPaymentAmount !== null || overPaymentAmount !== null)
        //         {
        //             store.card_color = 'warning';
        //             store.detail_icon_color = 'dark';
        //             store.detail_icon = 'cash-outline';
        //         }
        //
        //         store.has_preorder = await this.preorderedProductRepository.isTherePreorderedProductsForTheStoreRoute(store, route);
        //         store.time_spent = this.getTimeSpent(todayCheckIn);
        //         store.payment = await this.paymentRepository.getPaymentByCheckIn(todayCheckIn.id, store.id, route.id);
        //
        //         const cartItems = await this.cartRepository.loadCartItems(todayCheckIn.id, store, route);
        //         store.sales_volume = cartItems.reduce((acc, item) => acc + item.quantity, 0);
        //
        //         const returnItems = await this.returnRepository.loadReturnItems(todayCheckIn.id, store, route);
        //         store.returns_volume = returnItems.reduce((acc, item) => acc + item.quantity, 0);
        //     }
        //
        //     store.today_check_in = todayCheckIn;
        // }

        // Sort stores: those with card_color = 'success' should be at the bottom
        return stores.sort((a, b) =>
        {
            if (a.card_color === 'success' && b.card_color !== 'success')
            {
                return 1; // Move 'success' stores to the end
            }

            if (a.card_color !== 'success' && b.card_color === 'success')
            {
                return -1; // Keep non-'success' stores at the beginning
            }

            // If both have same card_color status, maintain original sort_order
            return a.sort_order - b.sort_order;
        });
    }

    async saveStores(stores: StoreModel[]): Promise<void>
    {
        const columns = [
            'id',
            'external_id',
            'name',
            'crates',
            'latitude',
            'longitude',
            'contact_name',
            'contact_phone',
            'contact_email',
            'postal_code',
            'is_check_in_only',
            'address_line_1',
            'address_line_2',
            'city',
            'province',
        ];
        const batchSize = Math.floor(3000 / columns.length);

        for (let i = 0; i < stores.length; i += batchSize)
        {
            const chunk = stores.slice(i, i + batchSize);
            const valuePlaceholders = this.sqliteService.getValuePlaceholder(chunk.length, columns.length);
            const sql = `
                INSERT INTO ${this.keys.tableName} (${columns.join(', ')})
                VALUES ${valuePlaceholders} ON CONFLICT(id) DO
                UPDATE
                    SET
                        external_id = excluded.external_id,
                    crates = excluded.crates,
                    latitude = excluded.latitude,
                    longitude = excluded.longitude,
                    contact_name = excluded.contact_name,
                    postal_code = excluded.postal_code,
                    is_check_in_only = excluded.is_check_in_only,
                    contact_phone = excluded.contact_phone,
                    contact_email = excluded.contact_email,
                    address_line_1 = excluded.address_line_1,
                    address_line_2 = excluded.address_line_2,
                    city = excluded.city,
                    province = excluded.province,
                    name = excluded.name;
            `;
            const values = chunk.flatMap((s) => [
                s.id,
                s.external_id,
                s.name,
                s.crates || 0,
                s.latitude,
                s.longitude,
                s.contact_name || null,
                s.contact_phone || null,
                s.contact_email || null,
                s.postal_code || null,
                s.is_check_in_only ? 1 : 0,
                s.address_line_1 || null,
                s.address_line_2 || null,
                s.city || null,
                s.province || null,
            ]);
            await this.sqliteService.save(sql, values);
        }
    }

    private getTimeSpent(checkIn: CheckInModel): string
    {
        if (!checkIn.check_in || !checkIn.check_out)
        {
            return '00:00:00';
        }

        const checkInDate = new Date(checkIn.check_in);
        const checkOutDate = new Date(checkIn.check_out);

        const totalSeconds = differenceInSeconds(checkOutDate, checkInDate);

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
}
